import { useState, useEffect, useCallback } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  X,
} from 'lucide-react'
import api from '@/admin/lib/api'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { useFormatters } from '@/shared/lib/formatting'
import { Input } from '@/shared/components/ui/input'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { DataTable } from '@/admin/components/data-table/data-table'
import type { DataTableColumn } from '@/admin/components/data-table/data-table'

/* ── Types ─────────────────────────────────────────────── */

import { toastApiError } from '@/shared/lib/api-error'
interface CommunicationLog {
  id: number
  channel: string
  email?: string
  phone?: string
  subject?: string
  status: string
  created_at: string
  tenant_slug?: string
  tenant_name?: string
  [key: string]: unknown
}

/** One row of the repeat-send summary: the same template to the same address. */
interface RepeatGroup {
  email: string | null
  phone: string | null
  template: string
  tenant: string
  total: number
  dispatched: number
  first_at: string
  last_at: string
}

interface TenantOption {
  slug: string
  name: string
}

/* ── Component ─────────────────────────────────────────── */

export function CommunicationLogsPage() {
  const { formatDateTime } = useFormatters()

  const [logs, setLogs] = useState<CommunicationLog[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [expandedDetail, setExpandedDetail] = useState<Record<string, unknown> | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  /* Filters */
  const [channelFilter, setChannelFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [emailSearch, setEmailSearch] = useState('')
  const [limitFilter, setLimitFilter] = useState(50)
  const [tenantFilter, setTenantFilter] = useState('')
  const [sinceFilter, setSinceFilter] = useState('')
  const [untilFilter, setUntilFilter] = useState('')

  /* Repeat-send summary */
  const [tab, setTab] = useState('recent')
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [groups, setGroups] = useState<RepeatGroup[]>([])
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [groupsFetching, setGroupsFetching] = useState(false)
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const [daysFilter, setDaysFilter] = useState(7)
  const [minCountFilter, setMinCountFilter] = useState(2)

  /* ── Fetch logs ─────────────────────────────────────────── */

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (channelFilter) params.set('channel', channelFilter)
      if (statusFilter) params.set('status', statusFilter)
      if (emailSearch) params.set('email', emailSearch)
      if (tenantFilter) params.set('organisation__tenant__slug', tenantFilter)
      if (sinceFilter) params.set('created_at__gte', sinceFilter)
      // A bare date means midnight, which would exclude the chosen day.
      if (untilFilter)
        params.set('created_at__lte', `${untilFilter}T23:59:59`)
      params.set('limit', String(limitFilter))
      const res = await api.get(`/staff/communication-logs/?${params.toString()}`)
      const data = Array.isArray(res.data) ? res.data : res.data.results ?? []
      setLogs(data)
    } catch (err) {
      toastApiError(err, 'Failed to load communication logs');
    } finally {
      setLoading(false)
    }
  }, [
    channelFilter,
    statusFilter,
    emailSearch,
    limitFilter,
    tenantFilter,
    sinceFilter,
    untilFilter,
  ])

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchLogs()
    }, emailSearch ? 300 : 0)
    return () => clearTimeout(timeout)
  }, [fetchLogs, emailSearch])

  /* ── Tenant options (for the tenant filter) ─────────────── */

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/staff/tenants/?limit=200&ordering=name')
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data?.results ?? [])
        if (cancelled) return
        setTenants(
          rows
            .filter((t: TenantOption) => t?.slug)
            .map((t: TenantOption) => ({ slug: t.slug, name: t.name || t.slug })),
        )
      } catch {
        // A missing tenant list must not break the page - the filter simply
        // stays empty and every other control keeps working.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /* ── Repeat-send summary ────────────────────────────────── */

  const fetchGroups = useCallback(async () => {
    setGroupsFetching(true)
    if (!groupsLoaded) setGroupsLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('days', String(daysFilter))
      params.set('min_count', String(minCountFilter))
      if (tenantFilter) params.set('tenant', tenantFilter)
      if (channelFilter) params.set('channel', channelFilter)
      const res = await api.get(
        `/staff/communication-logs/summary/?${params.toString()}`,
      )
      // The api client's response interceptor already unwraps the
      // `result` envelope, so groups sit directly on res.data.
      setGroups(res.data?.groups ?? [])
      setGroupsLoaded(true)
    } catch (err) {
      toastApiError(err, 'Failed to load the repeat-send summary')
    } finally {
      setGroupsLoading(false)
      setGroupsFetching(false)
    }
  }, [daysFilter, minCountFilter, tenantFilter, channelFilter, groupsLoaded])

  useEffect(() => {
    if (tab === 'repeats') fetchGroups()
  }, [tab, fetchGroups])

  const groupColumns: DataTableColumn<RepeatGroup>[] = [
    {
      id: 'count',
      header: 'Count',
      sortable: false,
      cell: (row) => (
        <span className="font-mono text-[13px] font-medium text-[var(--ink)]">
          {row.total}
        </span>
      ),
    },
    {
      id: 'recipient',
      header: 'Recipient',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink)]">
          {row.email || row.phone || '-'}
        </span>
      ),
    },
    {
      id: 'tenant',
      header: 'Tenant',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {row.tenant || 'Unattributed'}
        </span>
      ),
    },
    {
      id: 'template',
      header: 'Template',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {row.template || '-'}
        </span>
      ),
    },
    {
      id: 'dispatched',
      header: 'Delivered',
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink-3)]">
          {row.dispatched} of {row.total}
        </span>
      ),
    },
    {
      id: 'first_at',
      header: 'First',
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink-3)]">
          {formatDateTime(row.first_at)}
        </span>
      ),
    },
    {
      id: 'last_at',
      header: 'Last',
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink-3)]">
          {formatDateTime(row.last_at)}
        </span>
      ),
    },
  ]

  /* ── Expand row detail ──────────────────────────────────── */

  const toggleExpand = useCallback(
    async (id: number) => {
      if (expandedId === id) {
        setExpandedId(null)
        setExpandedDetail(null)
        return
      }
      setExpandedId(id)
      setExpandedDetail(null)
      setLoadingDetail(true)
      try {
        const res = await api.get(`/staff/communication-logs/${id}/`)
        setExpandedDetail(res.data)
      } catch (err) {
        toastApiError(err, 'Failed to load log detail');
        setExpandedId(null)
      } finally {
        setLoadingDetail(false)
      }
    },
    [expandedId],
  )

  /* ── Status badge color ─────────────────────────────────── */

  function statusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
      case 'failed':
      case 'bounced':
        return 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
      case 'pending':
      case 'queue':
      case 'queued':
        return 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
      default:
        return 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
    }
  }

  /* ── Render ─────────────────────────────────────────────── */

  return (
    <StaffShell>
      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="clara-title mb-2">Communication Logs</h1>
        <p className="clara-small mb-8">
          View all outgoing email and SMS communications.
        </p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList variant="line" className="mb-6">
            <TabsTrigger value="recent">Recent</TabsTrigger>
            <TabsTrigger value="repeats">Repeats</TabsTrigger>
          </TabsList>

          <TabsContent value="recent">
        {/* Filters */}
        <div
          className="mb-6 flex flex-wrap items-end gap-4 border-b-[0.5px] border-[var(--rule)] pb-6"
        >
          {/* Channel */}
          <div>
            <Label className="clara-label block mb-1.5">Channel</Label>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger
                className="min-w-[140px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              >
                <SelectValue placeholder="All channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All channels</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <Label className="clara-label block mb-1.5">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="min-w-[140px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              >
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Email search */}
          <div className="flex-1 min-w-[200px] max-w-sm">
            <Label className="clara-label block mb-1.5">Email Search</Label>
            <div className="relative">
              <SearchInput
                value={emailSearch}
                onChange={setEmailSearch}
                placeholder="Search by recipient email..."
                className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] py-2 pr-8 text-[13px] text-[var(--ink)] outline-none transition-all duration-150 focus:border-[var(--green)]"
              />
              {emailSearch && (
                <Button
                  variant="ghost"
                  onClick={() => setEmailSearch('')}
                  className="absolute right-3 top-1/2 cursor-pointer -translate-y-1/2 text-[var(--ink-3)]"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Tenant */}
          <div>
            <Label className="clara-label block mb-1.5">Tenant</Label>
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger className="min-w-[160px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]">
                <SelectValue placeholder="All tenants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All tenants</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.slug} value={t.slug}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date range */}
          <div>
            <Label className="clara-label block mb-1.5">From</Label>
            <Input
              type="date"
              value={sinceFilter}
              onChange={(e) => setSinceFilter(e.target.value)}
              className="w-[150px] rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
            />
          </div>
          <div>
            <Label className="clara-label block mb-1.5">To</Label>
            <Input
              type="date"
              value={untilFilter}
              onChange={(e) => setUntilFilter(e.target.value)}
              className="w-[150px] rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
            />
          </div>

          {/* Limit */}
          <div>
            <Label className="clara-label block mb-1.5">Limit</Label>
            <Select value={String(limitFilter)} onValueChange={(v: string) => setLimitFilter(Number(v))}>
              <SelectTrigger
                className="min-w-[90px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : logs.length === 0 ? (
          <p className="clara-small py-10 text-center">No communication logs found.</p>
        ) : (
          <div
            className="overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)]"
          >
            {/* Header row */}
            <div
              className="grid grid-cols-[32px_90px_minmax(0,1fr)_minmax(0,1fr)_100px_160px] items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3"
            >
              <span />
              <span className="clara-th">Channel</span>
              <span className="clara-th">Recipient</span>
              <span className="clara-th">Subject</span>
              <span className="clara-th">Status</span>
              <span className="clara-th">Created</span>
            </div>

            {/* Rows */}
            {logs.map((log) => {
              const isExpanded = expandedId === log.id
              const ss = statusClass(log.status)
              return (
                <div key={log.id}>
                  <div
                    className={cn(
                      'grid grid-cols-[32px_90px_minmax(0,1fr)_minmax(0,1fr)_100px_160px] items-center gap-3 border-b-[0.5px] border-[var(--rule)] px-4 py-3 transition-colors duration-150 cursor-pointer',
                      isExpanded ? 'bg-[var(--bg-subtle)]' : 'hover:bg-[var(--bg-subtle)]',
                    )}
                    onClick={() => toggleExpand(log.id)}
                  >
                    <span className="text-[var(--ink-3)]">
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </span>
                    <span
                      className="text-[11px] uppercase tracking-[0.08em] text-[var(--ink-2)]"
                    >
                      {log.channel}
                    </span>
                    <span
                      className="truncate text-[13px] text-[var(--ink)]"
                    >
                      {log.email || log.phone || '-'}
                    </span>
                    <span
                      className="truncate text-[13px] text-[var(--ink-2)]"
                    >
                      {log.subject || '-'}
                    </span>
                    <span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]',
                          ss,
                        )}
                      >
                        {log.status}
                      </span>
                    </span>
                    <span
                      className="text-[12px] text-[var(--ink-3)]"
                    >
                      {formatDateTime(log.created_at)}
                    </span>
                  </div>
                  {/* Expanded detail */}
                  {isExpanded && (
                    <div
                      className="border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-12 py-4"
                    >
                      {loadingDetail ? (
                        <div className="flex items-center gap-2 py-4">
                          <Loader2
                            className="h-3.5 w-3.5 animate-spin text-[var(--ink-3)]"
                          />
                          <span className="clara-small">Loading detail...</span>
                        </div>
                      ) : expandedDetail ? (
                        <pre
                          className="max-h-[400px] overflow-x-auto overflow-y-auto rounded-[2px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4 font-mono text-[11px] leading-relaxed text-[var(--ink-2)]"
                        >
                          {JSON.stringify(expandedDetail, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
          </TabsContent>

          <TabsContent value="repeats">
            <p className="clara-small mb-6">
              The same template sent repeatedly to one address. A row-per-message
              list cannot show this - you would have to page through every copy.
            </p>

            <div
              className="mb-6 flex flex-wrap items-end gap-4 border-b-[0.5px] border-[var(--rule)] pb-6"
            >
              <div>
                <Label className="clara-label block mb-1.5">Window</Label>
                <Select
                  value={String(daysFilter)}
                  onValueChange={(v: string) => setDaysFilter(Number(v))}
                >
                  <SelectTrigger className="min-w-[130px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 24 hours</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="clara-label block mb-1.5">Minimum Copies</Label>
                <Select
                  value={String(minCountFilter)}
                  onValueChange={(v: string) => setMinCountFilter(Number(v))}
                >
                  <SelectTrigger className="min-w-[110px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                    <SelectItem value="10">10+</SelectItem>
                    <SelectItem value="50">50+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="clara-label block mb-1.5">Tenant</Label>
                <Select value={tenantFilter} onValueChange={setTenantFilter}>
                  <SelectTrigger className="min-w-[160px] cursor-pointer rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]">
                    <SelectValue placeholder="All tenants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All tenants</SelectItem>
                    {tenants.map((t) => (
                      <SelectItem key={t.slug} value={t.slug}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DataTable<RepeatGroup>
              tableId="staff-communication-log-repeats"
              columns={groupColumns}
              data={groups}
              isLoading={groupsLoading}
              isFetching={groupsFetching}
              rowKey={(row) =>
                `${row.email || row.phone}|${row.template}|${row.tenant}`
              }
              emptyMessage="No repeated sends in this window."
            />
          </TabsContent>
        </Tabs>
      </div>
    </StaffShell>
  );
}
